import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Spinner } from 'reactstrap';
import { ToastContainer } from 'react-toastify';
import bg from '../assets/img/bg/BG.png';
import logo from '../assets/img/logo/Business-River-Logo.png';
import CustomToast from '../utils/CustomToast';
import { rememberInvite, forgetInvite, fetchInvite, acceptInvite, isRetryableInviteError } from '../utils/entryInvite';
import '../styles/stripe/stripe.css';

const STATE_MESSAGES = {
  accepted: 'This invite has already been used.',
  cancelled: 'This invite was cancelled by the entry owner.',
  expired: 'This invite has expired. Ask the entry owner to send you a new one.',
  invalid: 'This invite link is not valid.',
};

// Landing page for the link in an entry invite email (/entrant/invite?token=...)
const EntrantInviteAcceptPage = () => {
  const query = new URLSearchParams(useLocation().search);
  const inviteToken = query.get('token');
  const history = useHistory();

  const [cookies] = useCookies(['currentUser', 'token']);
  const isLoggedIn = !!(cookies.currentUser && cookies.token);

  const [invite, setInvite] = useState(null);
  const [problem, setProblem] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      setProblem(STATE_MESSAGES.invalid);
      return;
    }

    fetchInvite(inviteToken)
      .then(({ data }) => {
        if (data.status !== 'Success') {
          forgetInvite();
          setProblem(STATE_MESSAGES.invalid);
        } else if (data.data.state !== 'pending') {
          forgetInvite();
          setProblem(STATE_MESSAGES[data.data.state] || STATE_MESSAGES.invalid);
        } else {
          // Kept so the invite is accepted automatically after they register or log in
          rememberInvite(inviteToken);
          setInvite(data.data);
        }
      })
      .catch(() => setProblem('Something went wrong. Please check your internet connection and try again.'));
  }, [inviteToken]);

  const handleAccept = () => {
    setAccepting(true);
    acceptInvite(inviteToken, cookies.currentUser, cookies.token)
      .then(({ data }) => {
        if (data.status === 'Success') {
          forgetInvite();
          CustomToast.success(`You now have access to ${data.data.entry_name || 'the entry'}.`);
          history.push(`/entrant/entry/details?entry_id=${data.data.entry_id}`);
        } else {
          if (!isRetryableInviteError(data.code)) forgetInvite();
          CustomToast.error((data.errors && data.errors[0]) || 'Could not accept the invite.');
          setAccepting(false);
        }
      })
      .catch(() => {
        CustomToast.defaultError();
        setAccepting(false);
      });
  };

  const awardQuery = invite && invite.award_id ? `award_id=${invite.award_id}&` : '';
  const emailQuery = invite ? `email=${encodeURIComponent(invite.email)}` : '';

  return (
    <>
      <ToastContainer style={{ width: '370px' }} />
      <div id="hero-bussiness-river" style={{ backgroundImage: 'url(' + bg + ')' }}>
        <div className="DemoWrapper" style={{ maxWidth: '640px' }}>
          <div className="Demo text-center">
            <img src={logo} alt="BusinessRiver" className="bussiness-river-logo my-3" style={{ height: '80px' }} />

            {problem ? (
              <>
                <h1 className="text-dark" style={{ fontSize: '24px' }}>Invite unavailable</h1>
                <p>{problem}</p>
              </>
            ) : !invite ? (
              <Spinner />
            ) : (
              <>
                <h1 className="text-dark" style={{ fontSize: '24px' }}>You've been invited</h1>
                <p>
                  {invite.inviter_name || 'An entrant'} has invited you to work on <strong>{invite.entry_name || 'an entry'}</strong>
                  {invite.award_name ? <> for <strong>{invite.award_name}</strong></> : null}.
                </p>
                <p className="mb-4">The invite is for <strong>{invite.email}</strong>.</p>

                {isLoggedIn ? (
                  <>
                    <button type="button" className="btn newbtntheme-main primary-btn no-shadow px-5" onClick={handleAccept} disabled={accepting}>
                      {accepting ? <Spinner size="sm" /> : 'ACCEPT INVITE'}
                    </button>
                    <br />
                    <button type="button" className="btn btn-link" onClick={() => history.push('/logout')}>
                      Not {invite.email}? Log out
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn newbtntheme-main primary-btn no-shadow px-5 mb-2" onClick={() => history.push(`/entrant/register?${awardQuery}${emailQuery}`)}>
                      CREATE ACCOUNT
                    </button>
                    <br />
                    <button type="button" className="btn btn-link" onClick={() => history.push(`/entrant/login?${awardQuery}${emailQuery}`)}>
                      I already have an account
                    </button>
                    <p className="text-muted mt-3 mb-0">
                      <small>Use {invite.email}. You'll get access to the entry as soon as you're logged in.</small>
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EntrantInviteAcceptPage;
